import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  createUserDataBackup,
  getBackupAdvisory,
  markUserBackupDownloaded,
  parseAndValidateUserBackup,
  restoreUserDataBackup,
  type BackupAdvisory,
  type RestoreMode,
} from "../lib/browser-db/backup";
import {
  estimateBrowserStorage,
  getLastSuccessfulSyncRun,
  listDashboardNotices,
  requestPersistentStorage,
  updateUserNotice,
  type BrowserStorageEstimate,
  type UserNoticePatch,
} from "../lib/browser-db/repository";
import {
  getStoredCompanyProfile,
  reapplyStoredCompanyProfile,
  reevaluateAllNotices,
} from "../lib/browser-db/reevaluate";
import {
  openBrowserDatabase,
  type BrowserDatabase,
} from "../lib/browser-db/schema";
import type { CompanyProfile } from "../lib/evaluation/profile";
import {
  DatasetSchemaVersionError,
  synchronizeDataset,
} from "../lib/browser-db/sync";
import type {
  DashboardNotice,
  DatasetManifest,
  ReviewStatus,
} from "../lib/notices/types";
import { htmlToPlainText } from "../lib/notices/html-text";
import { validateOfficialUrl } from "../lib/validation/url";
import {
  DEFAULT_FILTERS,
  filterNotices,
  noticeRegion,
  priorityNotices,
  sortNotices,
  type DashboardFilters,
  type NoticeSort,
} from "./dashboard";
import {
  ABOUT_GRADE_ROWS,
  ABOUT_SCORE_ROWS,
  dismissAboutPermanently,
  markAboutShown,
  shouldAutoShowAbout,
} from "./about";
import { AccountControl } from "./auth";
import {
  cloneDefaultCompanyProfile,
  draftToProfile,
  profileToDraft,
  type CompanyProfileDraft,
} from "./company-settings";
import { buildBookmarkedNoticesMarkdown } from "./user-report";
import {
  applyUiZoom,
  matchZoomShortcut,
  PAGE_SIZE_OPTIONS,
  readStoredPageSize,
  readStoredUiZoom,
  UI_ZOOM_DEFAULT,
  UI_ZOOM_STEP,
  writeStoredPageSize,
  type PageSizeOption,
} from "./ui-prefs";

const REVIEW_STATUSES: ReviewStatus[] = [
  "미검토",
  "검토중",
  "문의필요",
  "제안준비",
  "보류",
  "제외",
];

type SyncState = "opening" | "local" | "syncing" | "ready" | "offline" | "error";

interface UserDraft {
  reviewStatus: ReviewStatus;
  assignee: string;
  note: string;
  hidden: boolean;
}

const EMPTY_DRAFT: UserDraft = {
  reviewStatus: "미검토",
  assignee: "",
  note: "",
  hidden: false,
};

function formatDate(value: string | null | undefined, includeTime = false): string {
  if (!value) return "확인 필요";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "확인 필요";
  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

function formatMoney(value: DashboardNotice["supportAmount"]): string {
  if (!value) return "공고문 확인";
  if (value.unit === "percent" && value.amountKrw !== null) return `${value.amountKrw}%`;
  if (value.unit === "KRW" && value.amountKrw !== null) {
    if (value.amountKrw >= 100_000_000) return `${(value.amountKrw / 100_000_000).toLocaleString("ko-KR")} 억원`;
    if (value.amountKrw >= 10_000) return `${(value.amountKrw / 10_000).toLocaleString("ko-KR")} 만원`;
    return `${value.amountKrw.toLocaleString("ko-KR")} 원`;
  }
  return value.raw || "공고문 확인";
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "없음";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function ddayLabel(notice: DashboardNotice): string {
  if (notice.status === "마감") return "마감";
  if (notice.dday === null) return "일정 확인";
  if (notice.dday === 0) return "D-DAY";
  if (notice.dday > 0) return `D-${notice.dday}`;
  return "마감";
}

function downloadText(fileName: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function userFromDraft(draft: UserDraft): UserNoticePatch {
  return {
    reviewStatus: draft.reviewStatus,
    assignee: draft.assignee,
    note: draft.note,
    hidden: draft.hidden,
  };
}

function categoryClass(category: DashboardNotice["category"]): string {
  if (category === "사업화") return "business";
  if (category === "지역") return "region";
  if (category === "조달") return "procurement";
  return "";
}

function scoreClass(score: number | undefined): string {
  if (score === undefined) return "conditional";
  if (score >= 85) return "excellent";
  if (score >= 70) return "good";
  if (score >= 50) return "conditional";
  return "bad";
}

function statusClass(status: DashboardNotice["status"]): string {
  if (status === "마감") return "closed";
  if (status === "접수예정") return "planned";
  if (status === "확인필요") return "unknown";
  return "";
}

function eligibilityClass(status: string): string {
  if (status === "불충족") return "fail";
  if (status === "미확인" || status === "조건부 충족") return "unknown";
  return "";
}

function App() {
  const databaseRef = useRef<BrowserDatabase | null>(null);
  const syncPromiseRef = useRef<Promise<void> | null>(null);
  const draftMetaRef = useRef<{ noticeId: string | null; dirty: boolean }>({
    noticeId: null,
    dirty: false,
  });
  const [notices, setNotices] = useState<DashboardNotice[]>([]);
  const [manifest, setManifest] = useState<DatasetManifest | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>("opening");
  const [dataMode, setDataMode] = useState<"실제" | "캐시" | "데모">("캐시");
  const [online, setOnline] = useState(() => navigator.onLine);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fatalKind, setFatalKind] = useState<"idb" | "schema" | null>(null);
  const [filters, setFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<NoticeSort>("deadline");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(() => readStoredPageSize(10));
  const [uiZoom, setUiZoom] = useState(() => readStoredUiZoom());
  const [showHidden, setShowHidden] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<UserDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [userMessage, setUserMessage] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const aboutAutoShownRef = useRef(false);
  const [storageEstimate, setStorageEstimate] = useState<BrowserStorageEstimate | null>(null);
  const [storagePersisted, setStoragePersisted] = useState<boolean | null>(null);
  const [backupAdvisory, setBackupAdvisory] = useState<BackupAdvisory | null>(null);
  const [restoreMode, setRestoreMode] = useState<RestoreMode>("merge");
  const [backupMessage, setBackupMessage] = useState<string | null>(null);
  const [companyDraft, setCompanyDraft] = useState<CompanyProfileDraft>(() =>
    profileToDraft(cloneDefaultCompanyProfile()),
  );
  const [storedProfile, setStoredProfile] = useState<CompanyProfile | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);

  const refreshLocal = useCallback(async (database: BrowserDatabase) => {
    const [localNotices, latestSync] = await Promise.all([
      listDashboardNotices(database, { includeHidden: true }),
      getLastSuccessfulSyncRun(database),
    ]);
    setNotices(localNotices);
    setLastSyncAt(latestSync?.finishedAt ?? null);
    setSelectedId((current) => {
      // 상세는 팝업(드로어)이므로 초기 로드·동기화에서 자동 선택하지 않는다.
      if (current && localNotices.some((notice) => notice.id === current)) return current;
      return null;
    });
    return localNotices;
  }, []);

  const runSync = useCallback((): Promise<void> => {
    if (syncPromiseRef.current) return syncPromiseRef.current;
    const database = databaseRef.current;
    if (!database) return Promise.resolve();
    if (!navigator.onLine) {
      setSyncState("offline");
      setErrorMessage("네트워크가 없어 마지막 정상 데이터를 표시합니다.");
      return Promise.resolve();
    }

    const operation = (async () => {
      setSyncState("syncing");
      setErrorMessage(null);
      setFatalKind(null);
      try {
        const result = await synchronizeDataset(database);
        setManifest(result.manifest);
        const sourceStates = Object.values(result.manifest.sources);
        setDataMode(
          sourceStates.length > 0 && sourceStates.every((source) => source.status === "demo")
            ? "데모"
            : "실제",
        );
        await refreshLocal(database);
        setSyncState("ready");
      } catch (error) {
        if (error instanceof DatasetSchemaVersionError) setFatalKind("schema");
        setErrorMessage(error instanceof Error ? error.message : "정적 데이터 동기화에 실패했습니다.");
        setSyncState("error");
      }
    })();
    syncPromiseRef.current = operation;
    void operation.finally(() => {
      if (syncPromiseRef.current === operation) syncPromiseRef.current = null;
    });
    return operation;
  }, [refreshLocal]);

  useEffect(() => {
    let active = true;
    const onOnline = () => {
      setOnline(true);
      void runSync();
    };
    const onOffline = () => {
      setOnline(false);
      setSyncState("offline");
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    void (async () => {
      try {
        const database = await openBrowserDatabase();
        if (!active) return;
        databaseRef.current = database;
        const local = await refreshLocal(database);
        setDataMode(local.length > 0 ? "캐시" : "데모");
        setSyncState(local.length > 0 ? "local" : "syncing");
        await runSync();
      } catch (error) {
        if (!active) return;
        setFatalKind("idb");
        setErrorMessage(error instanceof Error ? error.message : "이 기기 저장소를 열 수 없습니다.");
        setSyncState("error");
      }
    })();

    return () => {
      active = false;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshLocal, runSync]);

  const selected = useMemo(
    () => notices.find((notice) => notice.id === selectedId) ?? null,
    [notices, selectedId],
  );

  useEffect(() => {
    const selectedNoticeId = selected?.id ?? null;
    const changedNotice = draftMetaRef.current.noticeId !== selectedNoticeId;
    if (!changedNotice && draftMetaRef.current.dirty) return;
    draftMetaRef.current = { noticeId: selectedNoticeId, dirty: false };
    setDraft(
      selected?.user
        ? {
            reviewStatus: selected.user.reviewStatus,
            assignee: selected.user.assignee,
            note: selected.user.note,
            hidden: selected.user.hidden,
          }
        : EMPTY_DRAFT,
    );
  }, [selected?.id, selected?.user?.updatedAt]);

  const changeDraft = (patch: Partial<UserDraft>) => {
    draftMetaRef.current.dirty = true;
    setDraft((current) => ({ ...current, ...patch }));
  };

  const updateFilter = <K extends keyof DashboardFilters>(key: K, value: DashboardFilters[K]) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const showAllNotices = () => {
    setShowHidden(false);
    setFilters(DEFAULT_FILTERS);
    setSort("deadline");
    setPage(1);
    setMobileNavOpen(false);
  };

  const showBookmarkedNotices = () => {
    setShowHidden(false);
    setFilters({ ...DEFAULT_FILTERS, change: "bookmarked" });
    setSort("deadline");
    setPage(1);
    setMobileNavOpen(false);
  };

  const showHiddenNotices = () => {
    setShowHidden(true);
    setFilters(DEFAULT_FILTERS);
    setSort("deadline");
    setPage(1);
    setMobileNavOpen(false);
  };

  const showFitRanking = () => {
    setShowHidden(false);
    setFilters(DEFAULT_FILTERS);
    setSort("fit");
    setPage(1);
    setMobileNavOpen(false);
  };

  const openAbout = () => {
    setAboutOpen(true);
    setMobileNavOpen(false);
  };

  const closeAbout = useCallback((options?: { dismissForever?: boolean }) => {
    if (options?.dismissForever) dismissAboutPermanently();
    setAboutOpen(false);
  }, []);

  const listedNotices = useMemo(
    () => notices.filter((notice) => showHidden ? notice.user?.hidden : !notice.user?.hidden),
    [notices, showHidden],
  );
  const filtered = useMemo(
    () => sortNotices(filterNotices(listedNotices, filters), sort),
    [filters, listedNotices, sort],
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const visibleNotices = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const priorities = useMemo(() => priorityNotices(listedNotices), [listedNotices]);
  const categoryOptions = useMemo(() => [...new Set(listedNotices.map((notice) => notice.category))].sort(), [listedNotices]);
  const regionOptions = useMemo(() => [...new Set(listedNotices.map(noticeRegion))].sort(), [listedNotices]);
  const sourceOptions = useMemo(() => [...new Set(listedNotices.flatMap((notice) => notice.sourceNames))].sort(), [listedNotices]);

  const selectNotice = async (notice: DashboardNotice) => {
    if (
      draftMetaRef.current.dirty &&
      draftMetaRef.current.noticeId !== notice.id &&
      !window.confirm("저장하지 않은 메모·검토 내용을 버리고 다른 공고를 열까요?")
    ) {
      return;
    }
    setSelectedId(notice.id);
    setUserMessage(null);
    const database = databaseRef.current;
    if (!database) return;
    await updateUserNotice(
      database,
      notice.id,
      { lastOpenedAt: new Date().toISOString() },
      { sourceNoticeIds: notice.sourceNoticeIds },
    );
  };

  const mutateUserNotice = async (notice: DashboardNotice, patch: UserNoticePatch) => {
    const database = databaseRef.current;
    if (!database) return;
    setSaving(true);
    try {
      await updateUserNotice(database, notice.id, patch, {
        sourceNoticeIds: notice.sourceNoticeIds,
      });
      await refreshLocal(database);
      setUserMessage(null);
      return true;
    } catch (error) {
      setUserMessage(
        `로컬 검토 기록 저장 실패: ${error instanceof Error ? error.message : "이 기기 저장소 상태를 확인하세요."}`,
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (!selected) return;
    if (await mutateUserNotice(selected, userFromDraft(draft))) {
      draftMetaRef.current.dirty = false;
      setUserMessage("검토 상태와 메모를 이 브라우저에 저장했습니다.");
    }
  };

  const openSettings = async () => {
    setMobileNavOpen(false);
    setSettingsOpen(true);
    setBackupMessage(null);
    setProfileMessage(null);
    const database = databaseRef.current;
    if (!database) {
      setCompanyDraft(profileToDraft(cloneDefaultCompanyProfile()));
      setStoredProfile(null);
      return;
    }
    const [estimate, advisory, profile] = await Promise.all([
      estimateBrowserStorage(),
      getBackupAdvisory(database),
      getStoredCompanyProfile(database),
    ]);
    setStorageEstimate(estimate);
    setBackupAdvisory(advisory);
    const next = profile ?? cloneDefaultCompanyProfile();
    setStoredProfile(profile);
    setCompanyDraft(profileToDraft(next));
  };

  const updateCompanyDraft = <K extends keyof CompanyProfileDraft>(key: K, value: CompanyProfileDraft[K]) => {
    setCompanyDraft((current) => ({ ...current, [key]: value }));
  };

  const resetCompanyDraft = () => {
    setCompanyDraft(profileToDraft(cloneDefaultCompanyProfile()));
    setProfileMessage("에이아이큐브 기본값으로 폼을 채웠습니다. 저장해야 목록에 반영됩니다.");
  };

  const saveCompanyProfile = async () => {
    const database = databaseRef.current;
    if (!database) return;
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const profile = draftToProfile(companyDraft, storedProfile);
      const result = await reevaluateAllNotices(database, profile);
      setStoredProfile(await getStoredCompanyProfile(database));
      setCompanyDraft(profileToDraft((await getStoredCompanyProfile(database)) ?? profile));
      await refreshLocal(database);
      setProfileMessage(
        `${result.noticeCount}건을 재평가했습니다 · 프로필 ${result.profileVersion}`,
      );
    } catch (error) {
      setProfileMessage(
        `재평가 실패: ${error instanceof Error ? error.message : "이 기기 저장소 상태를 확인하세요."}`,
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const requestPersistence = async () => {
    const database = databaseRef.current;
    if (!database) return;
    const result = await requestPersistentStorage(database);
    setStoragePersisted(result.persisted);
    setBackupMessage(
      result.supported
        ? result.persisted
          ? "Chrome이 이 사이트의 로컬 데이터를 지속 저장하도록 설정했습니다."
          : "지속 저장소 요청이 승인되지 않았습니다. 정기 백업을 권장합니다."
        : "이 브라우저는 지속 저장소 API를 지원하지 않습니다.",
    );
  };

  const exportBackup = async () => {
    const database = databaseRef.current;
    if (!database) return;
    try {
      const backup = await createUserDataBackup(database);
      downloadText(backup.fileName, backup.json, "application/json;charset=utf-8");
      const timestamp = new Date().toISOString();
      await markUserBackupDownloaded(database, timestamp);
      setBackupAdvisory(await getBackupAdvisory(database));
      setBackupMessage("관심 공고·메모·검토 상태 백업을 다운로드했습니다.");
    } catch (error) {
      setBackupMessage(error instanceof Error ? error.message : "백업을 만들지 못했습니다.");
    }
  };

  const importBackup = async (file: File) => {
    const database = databaseRef.current;
    if (!database) return;
    setBackupMessage(null);
    try {
      if (
        restoreMode === "replace" &&
        !window.confirm("현재 로컬 사용자 데이터를 백업 파일로 교체할까요?")
      ) {
        return;
      }
      const backupText = await file.text();
      await parseAndValidateUserBackup(backupText);
      const preRestoreBackup = await createUserDataBackup(database);
      // Trigger the recoverable pre-restore file before any IndexedDB mutation.
      // restoreUserDataBackup validates again and creates its own in-memory
      // snapshot immediately before the atomic write transaction.
      downloadText(
        `pre-restore-${preRestoreBackup.fileName}`,
        preRestoreBackup.json,
        "application/json;charset=utf-8",
      );
      const result = await restoreUserDataBackup(database, backupText, {
        mode: restoreMode,
      });
      await reapplyStoredCompanyProfile(database);
      const profile = await getStoredCompanyProfile(database);
      setStoredProfile(profile);
      setCompanyDraft(profileToDraft(profile ?? cloneDefaultCompanyProfile()));
      await refreshLocal(database);
      setBackupMessage(
        `복원 전 백업을 다운로드하고 ${result.restoredCount}건을 ${result.mode === "merge" ? "병합" : "교체"}했습니다.`,
      );
    } catch (error) {
      setBackupMessage(
        `복원 실패: ${error instanceof Error ? error.message : "백업 파일을 확인하세요."}`,
      );
    }
  };

  const exportImportantChangesMarkdown = () => {
    const important = notices
      .flatMap((notice) => notice.changes.map((change) => ({ notice, change })))
      .filter(({ change }) => change.importance === "매우 높음" || change.importance === "높음");
    const lines = [
      "# AI 지원사업 중요 변경",
      "",
      `- 생성시각: ${new Date().toISOString()}`,
      `- 중요 변경: ${important.length}건`,
      "",
      ...important.flatMap(({ notice, change }) => [
        `## ${notice.title}`,
        "",
        `- 출처: ${notice.sourceNames.join(", ")}`,
        `- 변경 필드: ${change.fieldName}`,
        `- 이전: ${formatValue(change.oldValue)}`,
        `- 현재: ${formatValue(change.newValue)}`,
        `- 탐지시각: ${change.detectedAt}`,
        "",
      ]),
    ];
    downloadText(
      `ai-support-important-changes-${new Date().toISOString().slice(0, 10)}.md`,
      `${lines.join("\n")}\n`,
      "text/markdown;charset=utf-8",
    );
  };

  const exportBookmarkedMarkdown = () => {
    const generatedAt = new Date();
    downloadText(
      `ai-support-bookmarked-notices-${generatedAt.toISOString().slice(0, 10)}.md`,
      buildBookmarkedNoticesMarkdown(notices, { generatedAt }),
      "text/markdown;charset=utf-8",
    );
    setBackupMessage("관심 공고 보고서를 다운로드했습니다.");
  };

  const activeNotices = notices.filter((notice) => !notice.user?.hidden && !notice.sourceMissing);
  const newCount = activeNotices.filter((notice) => Date.now() - Date.parse(notice.firstSeenAt) <= 7 * 86_400_000).length;
  const changedCount = activeNotices.filter((notice) =>
    notice.changes.some((change) => change.changeType !== "new"),
  ).length;
  const urgentCount = activeNotices.filter((notice) => notice.dday !== null && notice.dday >= 0 && notice.dday <= 7).length;
  const openCount = activeNotices.filter((notice) => notice.status === "접수중" || notice.status === "접수예정").length;
  const sourceSummary = manifest
    ? Object.entries(manifest.sources)
        .map(([name, state]) => `${name} ${state.status}`)
        .join(" · ")
    : "공고 목록 확인 중";
  const bookmarkedView = !showHidden && filters.change === "bookmarked";
  const fitRankingView = !showHidden && !bookmarkedView && sort === "fit";
  const monitorView = !showHidden && !bookmarkedView && !fitRankingView;

  const fatalAction = fatalKind === "schema"
    ? "새 버전으로 앱을 업데이트한 뒤 다시 시도하세요."
    : "브라우저 설정에서 사이트 데이터 저장을 허용한 뒤 새로고침하세요.";
  const sourceMissingBanner = "최신 목록에서 사라진 공고입니다. 기존 메모 보존을 위해 이 기기에 남겨두었으며 신청 전 공식 원문을 재확인하세요.";

  const closeDetail = () => {
    if (
      draftMetaRef.current.dirty &&
      !window.confirm("저장하지 않은 메모·검토 내용을 버리고 상세를 닫을까요?")
    ) {
      return;
    }
    draftMetaRef.current.dirty = false;
    setSelectedId(null);
    setUserMessage(null);
  };

  useEffect(() => {
    if (aboutAutoShownRef.current) return;
    if (syncState === "opening" || syncState === "syncing") return;
    if (!shouldAutoShowAbout()) return;
    aboutAutoShownRef.current = true;
    markAboutShown();
    setAboutOpen(true);
  }, [syncState]);

  useEffect(() => {
    applyUiZoom(uiZoom);
  }, [uiZoom]);

  useEffect(() => {
    const onZoomShortcut = (event: KeyboardEvent) => {
      const action = matchZoomShortcut(event);
      if (!action) return;
      event.preventDefault();
      setUiZoom((current) => {
        if (action === "reset") return applyUiZoom(UI_ZOOM_DEFAULT);
        if (action === "in") return applyUiZoom(current + UI_ZOOM_STEP);
        return applyUiZoom(current - UI_ZOOM_STEP);
      });
    };
    window.addEventListener("keydown", onZoomShortcut);
    return () => window.removeEventListener("keydown", onZoomShortcut);
  }, []);

  const changePageSize = (value: PageSizeOption) => {
    setPageSize(value);
    writeStoredPageSize(value);
    setPage(1);
  };

  useEffect(() => {
    if (!settingsOpen && !aboutOpen && !selectedId) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (settingsOpen) {
        setSettingsOpen(false);
        return;
      }
      if (aboutOpen) {
        closeAbout();
        return;
      }
      if (
        draftMetaRef.current.dirty &&
        !window.confirm("저장하지 않은 메모·검토 내용을 버리고 상세를 닫을까요?")
      ) {
        return;
      }
      draftMetaRef.current.dirty = false;
      setSelectedId(null);
      setUserMessage(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [settingsOpen, aboutOpen, selectedId, closeAbout]);

  useEffect(() => {
    if (!selectedId && !aboutOpen && !settingsOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [selectedId, aboutOpen, settingsOpen]);

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNavOpen ? "open" : ""}`} aria-label="주요 메뉴">
        <div className="brand" aria-label="AI Cube"><span>AI</span></div>
        <nav className="nav-list">
          <button className={`nav-button ${monitorView ? "active" : ""}`} type="button" aria-pressed={monitorView} onClick={showAllNotices}>
            <span className="icon">▦</span><span>공고 모니터</span><small>{notices.length}</small>
          </button>
          <button className={`nav-button ${bookmarkedView ? "active" : ""}`} type="button" aria-pressed={bookmarkedView} onClick={showBookmarkedNotices}>
            <span className="icon">★</span><span>관심 공고</span><small>{notices.filter((notice) => notice.user?.bookmarked).length}</small>
          </button>
          <button className={`nav-button ${showHidden ? "active" : ""}`} type="button" aria-pressed={showHidden} onClick={showHiddenNotices}>
            <span className="icon">⊘</span><span>숨김 공고</span><small>{notices.filter((notice) => notice.user?.hidden).length}</small>
          </button>
          <button className={`nav-button ${fitRankingView ? "active" : ""}`} type="button" aria-pressed={fitRankingView} onClick={showFitRanking}>
            <span className="icon">◈</span><span>적합도 순</span>
          </button>
          <button className={`nav-button ${aboutOpen ? "active" : ""}`} type="button" aria-pressed={aboutOpen} onClick={openAbout}>
            <span className="icon">ⓘ</span><span>이 모니터 안내</span>
          </button>
          <button className={`nav-button ${settingsOpen ? "active" : ""}`} type="button" aria-pressed={settingsOpen} onClick={() => void openSettings()}>
            <span className="icon">⚙</span><span>설정</span>
          </button>
        </nav>
        <div className="sidebar-footer">
          <button className="nav-button" type="button" disabled={syncState === "syncing"} onClick={() => void runSync()}>
            <span className="icon">↻</span><span>지금 동기화</span>
          </button>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <button className="mobile-menu" type="button" onClick={() => setMobileNavOpen((open) => !open)} aria-label="메뉴 열기">☰</button>
          <div className="title-block">
            <p>LOCAL-FIRST SUPPORT INTELLIGENCE</p>
            <h1>AI 지원사업 모니터</h1>
          </div>
          <label className="search">
            <span aria-hidden="true">⌕</span>
            <input
              value={filters.query}
              onChange={(event) => updateFilter("query", event.target.value)}
              placeholder="공고명·기관명 검색"
              aria-label="공고명과 기관명 검색"
            />
            {filters.query && <button type="button" onClick={() => updateFilter("query", "")} aria-label="검색어 지우기">×</button>}
          </label>
          <div className="sync-state" aria-live="polite">
            <span className={`dot ${!online ? "offline" : syncState}`}></span>
            <strong>{!online ? "오프라인" : syncState === "syncing" ? "동기화 중" : dataMode === "데모" ? "데모 모드" : "최신 공고"}</strong>
            <small>갱신 {formatDate(lastSyncAt, true)}</small>
          </div>
          <AccountControl />
        </header>

        {(!online || errorMessage) && (
          <div className={`notice-banner ${errorMessage ? "error" : ""}`} role={errorMessage ? "alert" : "status"}>
            <span>
              <strong>{!online ? "오프라인 모드" : "데이터 확인 필요"}</strong>
              {" · "}{errorMessage ?? "네트워크가 없어 이 기기에 저장된 공고를 표시합니다."}
            </span>
            <button type="button" disabled={syncState === "syncing"} onClick={() => void runSync()}>재시도</button>
          </div>
        )}

        <section className="kpi-grid" aria-label="공고 요약">
          <article className="kpi blue"><span className="kpi-icon">▦</span><div className="kpi-copy"><span>접수 가능</span><strong>{openCount}</strong><small>건</small></div></article>
          <article className="kpi green"><span className="kpi-icon">N</span><div className="kpi-copy"><span>7일 내 신규</span><strong>{newCount}</strong><small>건</small></div></article>
          <article className="kpi orange"><span className="kpi-icon">⇅</span><div className="kpi-copy"><span>변경 공고</span><strong>{changedCount}</strong><small>건</small></div></article>
          <article className="kpi red"><span className="kpi-icon">!</span><div className="kpi-copy"><span>7일 내 마감</span><strong>{urgentCount}</strong><small>건</small></div></article>
        </section>

        <section className="controls" aria-label="공고 필터와 정렬">
          <div className="filter-tabs" role="tablist" aria-label="사업 분야 빠른 필터">
            <button type="button" role="tab" aria-selected={filters.category === "all"} className={filters.category === "all" ? "active" : ""} onClick={() => updateFilter("category", "all")}>전체</button>
            {categoryOptions.map((value) => (
              <button type="button" role="tab" aria-selected={filters.category === value} className={filters.category === value ? "active" : ""} key={value} onClick={() => updateFilter("category", value)}>{value}</button>
            ))}
          </div>
          <div className="filter-row">
            <select value={filters.category} onChange={(event) => updateFilter("category", event.target.value)} aria-label="분야 필터"><option value="all">분야 전체</option>{categoryOptions.map((value) => <option key={value}>{value}</option>)}</select>
            <select value={filters.region} onChange={(event) => updateFilter("region", event.target.value)} aria-label="지역 필터"><option value="all">지역 전체</option>{regionOptions.map((value) => <option key={value}>{value}</option>)}</select>
            <select value={filters.source} onChange={(event) => updateFilter("source", event.target.value)} aria-label="출처 필터"><option value="all">출처 전체</option>{sourceOptions.map((value) => <option key={value}>{value}</option>)}</select>
            <select value={filters.eligibility} onChange={(event) => updateFilter("eligibility", event.target.value)} aria-label="신청자격 필터"><option value="all">신청자격 전체</option><option>충족</option><option>조건부 충족</option><option>미확인</option><option>불충족</option></select>
            <select value={filters.fit} onChange={(event) => updateFilter("fit", event.target.value)} aria-label="적합도 필터"><option value="all">적합도 전체</option><option>매우 적합</option><option>적합</option><option>조건부 적합</option><option>부적합 가능성 높음</option><option>사실상 부적합</option></select>
            <select value={filters.deadline} onChange={(event) => updateFilter("deadline", event.target.value as DashboardFilters["deadline"])} aria-label="마감일 필터"><option value="all">마감일 전체</option><option value="7">7일 이내</option><option value="30">30일 이내</option><option value="open">접수 가능</option><option value="closed">마감</option></select>
            <select value={filters.change} onChange={(event) => updateFilter("change", event.target.value as DashboardFilters["change"])} aria-label="신규 변경 필터"><option value="all">신규·변경 전체</option><option value="new">신규</option><option value="changed">변경</option><option value="bookmarked">관심</option></select>
            <button className="clear-filters" type="button" onClick={() => { setFilters(DEFAULT_FILTERS); setPage(1); }}>필터 초기화</button>
          </div>
          <div className="sort-row"><span>정렬</span><select value={sort} onChange={(event) => { setSort(event.target.value as NoticeSort); setPage(1); }} aria-label="정렬 기준"><option value="deadline">마감순</option><option value="fit">적합도순</option><option value="latest">최신순</option></select></div>
        </section>

        {fatalKind && notices.length === 0 ? (
          <section className="panel empty" role="alert">
            <div><strong>{fatalKind === "idb" ? "이 기기 저장소를 열지 못했습니다" : "데이터 업데이트가 필요합니다"}</strong><p>{fatalAction}</p><button type="button" onClick={() => window.location.reload()}>새로고침</button></div>
          </section>
        ) : (
          <section className="content-stack">
            <aside className="panel priority-panel" aria-label="오늘의 우선 공고">
              <div className="panel-heading"><div><h2>오늘의 우선 공고</h2><p>자격 불충족은 추천에서 제외됩니다. 항목을 누르면 상세가 열립니다.</p></div><span className="count-pill">TOP {priorities.length}</span></div>
              <div className="priority-list">
                {priorities.map((notice, index) => <button type="button" className={`priority-item ${notice.id === selectedId ? "active" : ""}`} key={notice.id} onClick={() => void selectNotice(notice)}><span className="rank">{index + 1}</span><span className="priority-copy"><strong>{notice.title}</strong><small>{notice.agency} · {ddayLabel(notice)}</small></span><b>{notice.evaluation?.fitScore ?? "-"}</b></button>)}
              </div>
            </aside>

            <article className="panel">
              <div className="panel-heading"><div><h2>{showHidden ? "숨김 공고" : "지원사업 공고"}</h2><p>검색·필터·정렬은 이 기기에 받은 공고 목록에 적용됩니다.</p></div><span className="count-pill">{filtered.length.toLocaleString("ko-KR")} / {listedNotices.length.toLocaleString("ko-KR")}건</span></div>
              {syncState === "opening" || (syncState === "syncing" && notices.length === 0) ? (
                <div className="empty"><div><strong>공고 목록을 준비하고 있습니다</strong><p>잠시만 기다려 주세요. 준비가 끝나면 목록이 표시됩니다.</p><button type="button" disabled={syncState === "syncing"} onClick={() => void runSync()}>다시 불러오기</button></div></div>
              ) : filtered.length === 0 ? (
                <div className="empty"><div><strong>조건에 맞는 공고가 없습니다</strong><p>{showHidden ? "숨긴 공고가 없거나 현재 조건과 일치하지 않습니다." : "검색어나 필터를 줄여 다시 확인하세요."}</p><button type="button" onClick={showAllNotices}>전체 공고 보기</button></div></div>
              ) : (
                <>
                  <div className="table-wrap">
                    <table className="notice-table">
                      <thead><tr><th>공고명</th><th>분야·지역</th><th>출처</th><th>적합도</th><th>마감</th><th>상태</th><th>관심</th></tr></thead>
                      <tbody>
                        {visibleNotices.map((notice) => (
                          <tr key={notice.id} className={`notice-row ${selectedId === notice.id ? "selected" : ""}`} tabIndex={0} aria-label={`${notice.title} 상세 열기`} onClick={() => void selectNotice(notice)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); void selectNotice(notice); } }}>
                            <td><div className="title-cell"><span className={`change-dot ${notice.changes.length > 0 ? "changed" : ""}`}></span><div className="title-copy"><strong>{notice.title}</strong><small>{notice.sourceMissing ? "출처에서 소실 · 원문 재확인 필요" : notice.agency}</small></div></div></td>
                            <td><span className={`tag ${categoryClass(notice.category)}`}>{notice.category}</span><br /><small>{noticeRegion(notice)}</small></td>
                            <td>{notice.sourceNames.join(", ")}</td>
                            <td><span className={`score ${scoreClass(notice.evaluation?.fitScore)}`}>{notice.evaluation ? `${notice.evaluation.fitScore}점` : "미평가"}</span></td>
                            <td><div className="deadline"><b className={notice.dday !== null && notice.dday >= 0 && notice.dday <= 7 ? "urgent" : ""}>{ddayLabel(notice)}</b><span>{formatDate(notice.applicationEndAt)}</span></div></td>
                            <td><span className={`status-badge ${notice.sourceMissing ? "unknown" : statusClass(notice.status)}`}>{notice.sourceMissing ? "출처 확인" : notice.status}</span></td>
                            <td><button className={`bookmark ${notice.user?.bookmarked ? "saved" : ""}`} type="button" aria-label={notice.user?.bookmarked ? "관심 해제" : "관심 등록"} onClick={(event) => { event.stopPropagation(); void mutateUserNotice(notice, { bookmarked: !notice.user?.bookmarked }); }}>{notice.user?.bookmarked ? "★" : "☆"}</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <footer className="table-footer">
                    <span>{(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filtered.length)}건 표시</span>
                    <div className="pagination"><button type="button" disabled={safePage === 1} onClick={() => setPage((value) => Math.max(1, value - 1))} aria-label="이전 페이지">‹</button>{Array.from({ length: Math.min(pageCount, 5) }, (_, index) => index + Math.max(1, Math.min(safePage - 2, pageCount - 4))).filter((value) => value <= pageCount).map((value) => <button type="button" key={value} className={safePage === value ? "active" : ""} onClick={() => setPage(value)}>{value}</button>)}<button type="button" disabled={safePage === pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))} aria-label="다음 페이지">›</button></div>
                    <label className="page-size">
                      <span>표시</span>
                      <select
                        value={pageSize}
                        aria-label="페이지당 공고 수"
                        onChange={(event) => changePageSize(Number(event.target.value) as PageSizeOption)}
                      >
                        {PAGE_SIZE_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}건</option>
                        ))}
                      </select>
                    </label>
                  </footer>
                </>
              )}
            </article>
          </section>
        )}
      </main>

      {selected && (
        <div
          className="detail-drawer-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) closeDetail();
          }}
        >
          <aside
            className="detail-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="detail-drawer-title"
          >
            <header className="detail-drawer-header">
              <div>
                <p className="detail-drawer-kicker">공고 상세</p>
                <h2 id="detail-drawer-title">{selected.title}</h2>
              </div>
              <button type="button" className="detail-drawer-close" onClick={closeDetail} aria-label="상세 닫기">×</button>
            </header>
            <div className="detail-drawer-body">
              <div className="detail-meta">
                <span className={`tag ${categoryClass(selected.category)}`}>{selected.category}</span>
                <span className={`score ${scoreClass(selected.evaluation?.fitScore)}`}>{selected.evaluation?.fitGrade ?? "미평가"}</span>
              </div>
              {selected.sourceMissing && <div className="notice-banner error"><span>{sourceMissingBanner}</span></div>}
              <section className="detail-section"><h3>공고 요약</h3><p className="detail-prose">{htmlToPlainText(selected.summary) || "요약이 없습니다. 원문을 확인하세요."}</p><p><strong>지원 규모:</strong> {formatMoney(selected.supportAmount)}</p><p><strong>신청 기간:</strong> {formatDate(selected.applicationStartAt)} ~ {formatDate(selected.applicationEndAt)}</p></section>
              <section className="detail-section"><h3>신청자격</h3><span className={`eligibility ${eligibilityClass(selected.evaluation?.eligibilityStatus ?? "미확인")}`}>{selected.evaluation?.eligibilityStatus ?? "미확인"}</span><p className="detail-prose">{htmlToPlainText(selected.eligibilityText) || "원문에서 신청자격을 확인하세요."}</p>{selected.evaluation?.eligibilityReasons.length ? <ul>{selected.evaluation.eligibilityReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul> : null}</section>
              <section className="detail-section"><h3>적합도 근거·위험</h3>{selected.evaluation ? <><ul>{selected.evaluation.fitReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>{selected.evaluation.risks.length > 0 && <ul>{selected.evaluation.risks.map((risk) => <li key={risk}>위험: {risk}</li>)}</ul>}<p><strong>권장 행동:</strong> {selected.evaluation.recommendedAction}</p>{selected.evaluation.requiredConfirmations.length > 0 && <ul>{selected.evaluation.requiredConfirmations.map((item) => <li key={item}>추가 확인: {item}</li>)}</ul>}</> : <p>규칙 평가 데이터가 없습니다.</p>}</section>
              <section className="detail-section"><h3>최근 변경 비교</h3>{selected.changes.length > 0 ? <ul>{selected.changes.slice(0, 5).map((change) => <li key={change.id}><strong>{change.fieldName}</strong>: {formatValue(change.oldValue)} → {formatValue(change.newValue)}</li>)}</ul> : <p>기록된 변경이 없습니다.</p>}</section>
              <section className="detail-section"><h3>첨부파일</h3>{selected.changes.some((change) => change.changeType === "attachment") && <p><strong>첨부파일 변경이 탐지됨</strong></p>}{selected.attachments.length > 0 ? <ul>{selected.attachments.map((attachment) => { const safe = validateOfficialUrl(attachment.sourceUrl); return <li key={`${attachment.fileName}-${attachment.sourceUrl}`}>{safe.valid && safe.normalizedUrl ? <a href={safe.normalizedUrl} target="_blank" rel="noreferrer">{attachment.fileName}</a> : attachment.fileName} · {attachment.extractionStatus}</li>; })}</ul> : <p>수집된 첨부파일이 없습니다.</p>}</section>
              <section className="detail-section"><h3>개인 검토 기록</h3><div className="user-form">{userMessage && <p role="status">{userMessage}</p>}<select value={draft.reviewStatus} onChange={(event) => changeDraft({ reviewStatus: event.target.value as ReviewStatus })} aria-label="검토 상태">{REVIEW_STATUSES.map((value) => <option key={value}>{value}</option>)}</select><input value={draft.assignee} onChange={(event) => changeDraft({ assignee: event.target.value })} placeholder="담당자" aria-label="담당자" /><textarea value={draft.note} onChange={(event) => changeDraft({ note: event.target.value })} placeholder="이 기기에만 저장되는 메모" aria-label="개인 메모" /><label><input type="checkbox" checked={draft.hidden} onChange={(event) => changeDraft({ hidden: event.target.checked })} /> {draft.hidden ? "숨김 해제 가능" : "목록에서 숨기기"}</label><button className="primary" type="button" disabled={saving} onClick={() => void saveDraft()}>{saving ? "저장 중" : "검토 기록 저장"}</button></div></section>
            </div>
            <footer className="detail-drawer-footer">
              {(() => {
                const safe = validateOfficialUrl(selected.originalUrl);
                return safe.valid && safe.normalizedUrl
                  ? <a className="primary" href={safe.normalizedUrl} target="_blank" rel="noreferrer">공식 원문 열기</a>
                  : <button className="primary" type="button" disabled>원문 URL 확인 필요</button>;
              })()}
              <button className="secondary" type="button" onClick={() => void mutateUserNotice(selected, { bookmarked: !selected.user?.bookmarked })}>{selected.user?.bookmarked ? "관심 해제" : "관심 등록"}</button>
              <button className="secondary" type="button" onClick={closeDetail}>닫기</button>
            </footer>
          </aside>
        </div>
      )}

      {aboutOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) closeAbout(); }}>
          <section className="modal about-modal" role="dialog" aria-modal="true" aria-labelledby="about-title">
            <header className="modal-header">
              <h2 id="about-title">이 모니터 안내</h2>
              <button type="button" onClick={() => closeAbout()} aria-label="닫기">×</button>
            </header>
            <div className="modal-body">
              <article className="setting-card">
                <h3>무엇을 위한 도구인가</h3>
                <p>
                  AI·소프트웨어 중심 스타트업이 매일 쏟아지는 지원사업 공고를 훑지 않고도,
                  우리 회사에 맞을 가능성이 높은 공고만 먼저 보고 마감·변경을 놓치지 않게 돕는 로컬 우선 모니터입니다.
                </p>
              </article>
              <article className="setting-card">
                <h3>자격 vs 적합도</h3>
                <p>
                  <strong>신청자격</strong>은 공고 요건을 통과할 수 있는지(통과/불확실/탈락 가능)이고,
                  <strong>적합도 점수</strong>는 그다음으로 “우리 사업과 얼마나 잘 맞는지”를 100점 만점으로 추정합니다.
                  자격 탈락 가능성이 커도 점수가 높게 나오지 않도록 배점을 분리합니다.
                </p>
              </article>
              <article className="setting-card">
                <h3>배점 (합계 100)</h3>
                <ul className="about-score-list">
                  {ABOUT_SCORE_ROWS.map((row) => (
                    <li key={row.label}><span>{row.label}</span><strong>{row.points}</strong></li>
                  ))}
                </ul>
              </article>
              <article className="setting-card">
                <h3>등급</h3>
                <ul className="about-score-list">
                  {ABOUT_GRADE_ROWS.map((row) => (
                    <li key={row.label}><span>{row.label}</span><strong>{row.range}</strong></li>
                  ))}
                </ul>
              </article>
              <article className="setting-card">
                <h3>평가·데이터 방식</h3>
                <p>
                  기본은 규칙으로 점수를 매깁니다. AI 분석은 기본으로 꺼져 있으며, 필요할 때만 켤 수 있습니다.
                  공고 목록은 서버에서 받아 이 기기에 보관하고, 관심·메모·검토는 이 기기에만 저장됩니다.
                </p>
                <p className="about-legal-links">
                  <a href="/legal/terms.html" target="_blank" rel="noreferrer">이용약관</a>
                  {" · "}
                  <a href="/legal/privacy.html" target="_blank" rel="noreferrer">개인정보처리방침</a>
                </p>
              </article>
              <div className="about-actions">
                <button type="button" className="secondary" onClick={() => closeAbout({ dismissForever: true })}>다시 보지 않기</button>
                <button type="button" onClick={() => closeAbout()}>확인</button>
              </div>
            </div>
          </section>
        </div>
      )}

      {settingsOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setSettingsOpen(false); }}>
          <section className="modal settings-modal" role="dialog" aria-modal="true" aria-labelledby="settings-title">
            <header className="modal-header"><h2 id="settings-title">설정</h2><button type="button" onClick={() => setSettingsOpen(false)} aria-label="닫기">×</button></header>
            <div className="modal-body">
              {(backupMessage || profileMessage) && (
                <div className={`notice-banner ${(backupMessage?.startsWith("복원 실패") || profileMessage?.startsWith("재평가 실패")) ? "error" : ""}`} role="status">
                  <span>{profileMessage ?? backupMessage}</span>
                </div>
              )}
              <article className="setting-card">
                <h3>회사 프로필</h3>
                <p>저장하면 이 기기의 적합도·자격이 다시 계산됩니다. 서버에 올라간 공고 원본은 바뀌지 않으며, 이후 동기화에도 내 회사 설정이 다시 적용됩니다.</p>
                <div className="company-form">
                  <label>회사명<input value={companyDraft.name} onChange={(event) => updateCompanyDraft("name", event.target.value)} /></label>
                  <label>기업 유형 (쉼표 구분)<input value={companyDraft.types} onChange={(event) => updateCompanyDraft("types", event.target.value)} /></label>
                  <label>본사 시·도<input value={companyDraft.province} onChange={(event) => updateCompanyDraft("province", event.target.value)} /></label>
                  <label>본사 시·군<input value={companyDraft.city} onChange={(event) => updateCompanyDraft("city", event.target.value)} /></label>
                  <label>직원 수<input inputMode="numeric" value={companyDraft.employeeCount} onChange={(event) => updateCompanyDraft("employeeCount", event.target.value)} /></label>
                  <label>업력(년)<input inputMode="numeric" value={companyDraft.businessYears} onChange={(event) => updateCompanyDraft("businessYears", event.target.value)} /></label>
                  <label>연매출(만원)<input inputMode="numeric" value={companyDraft.annualRevenueManwon} onChange={(event) => updateCompanyDraft("annualRevenueManwon", event.target.value)} /></label>
                  <label className="span-2">역량 (쉼표 구분)<textarea rows={2} value={companyDraft.capabilities} onChange={(event) => updateCompanyDraft("capabilities", event.target.value)} /></label>
                  <label className="span-2">타깃 시장 (쉼표 구분)<textarea rows={2} value={companyDraft.targetMarkets} onChange={(event) => updateCompanyDraft("targetMarkets", event.target.value)} /></label>
                  <label className="span-2">관심 지역 (쉼표 구분)<textarea rows={2} value={companyDraft.regionsOfInterest} onChange={(event) => updateCompanyDraft("regionsOfInterest", event.target.value)} /></label>
                  <label className="span-2">협력·파트너십 (쉼표 구분)<textarea rows={2} value={companyDraft.partnerships} onChange={(event) => updateCompanyDraft("partnerships", event.target.value)} /></label>
                </div>
                <div className="setting-actions">
                  <button type="button" disabled={profileSaving} onClick={() => void saveCompanyProfile()}>
                    {profileSaving ? "재평가 중…" : "프로필 저장·재평가"}
                  </button>
                  <button className="secondary" type="button" disabled={profileSaving} onClick={resetCompanyDraft}>기본값으로 되돌리기</button>
                </div>
                {storedProfile && <p>적용 중 버전: {storedProfile.version}</p>}
              </article>
              <article className="setting-card"><h3>이 기기 저장소</h3><p>공식 공고 목록과 관심·메모·검토 상태를 이 기기에 저장합니다. 브라우저 또는 사이트 데이터를 삭제하면 개인 데이터가 사라질 수 있습니다.</p>{storageEstimate?.supported && <><div className="storage-meter"><span style={{ width: `${Math.min(100, (storageEstimate.usageRatio ?? 0) * 100)}%` }}></span></div><p>{((storageEstimate.usage ?? 0) / 1024 / 1024).toFixed(2)} MB / {((storageEstimate.quota ?? 0) / 1024 / 1024).toFixed(0)} MB 사용</p></>}<div className="setting-actions"><button type="button" onClick={() => void requestPersistence()}>지속 저장소 요청</button><button className="secondary" type="button" onClick={() => void openSettings()}>용량 새로고침</button></div>{storagePersisted !== null && <p>지속 저장소: {storagePersisted ? "승인됨" : "미승인"}</p>}</article>
              <article className="setting-card"><h3>개인 데이터 JSON 백업</h3><p>{backupAdvisory?.deletionRisk ?? "브라우저 데이터 삭제에 대비해 정기적으로 백업하세요."} 마지막 백업: {formatDate(backupAdvisory?.lastBackupAt, true)}{backupAdvisory?.warningRequired ? " · 백업 필요" : ""}</p><div className="setting-actions"><button type="button" onClick={() => void exportBackup()}>백업 다운로드</button><select value={restoreMode} onChange={(event) => setRestoreMode(event.target.value as RestoreMode)} aria-label="복원 방식"><option value="merge">병합 복원</option><option value="replace">교체 복원</option></select><label className="file-button">백업 가져오기<input type="file" accept="application/json,.json" onChange={(event) => { const file = event.target.files?.[0]; if (file) void importBackup(file); event.target.value = ""; }} /></label></div></article>
              <article className="setting-card"><h3>Markdown 보고서</h3><p>관심 공고의 메모·검토 상태 또는 매우 높음·높음 변경만 사람이 읽기 쉬운 문서로 내보냅니다.</p><div className="setting-actions"><button type="button" onClick={exportBookmarkedMarkdown}>관심 공고 보고서</button><button className="secondary" type="button" onClick={exportImportantChangesMarkdown}>중요 변경 보고서</button></div></article>
              <article className="setting-card"><h3>데이터 출처 상태</h3><p>{sourceSummary}</p><p>목록 생성: {formatDate(manifest?.generatedAt, true)} · 이 기기 갱신: {formatDate(lastSyncAt, true)}</p></article>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default App;
